<template>
  <VApp class="bg-socio-grey" data-testid="app">
    <RouterView v-slot="{ Component }">
      <component :is="Component">
        <template #footer>
          <SocioFooter :items="useMainFooterItems()" data-testid="footer">
            <template #logo>
              <img
                height="56"
                width="56"
                src="@/assets/images/Socotec_certification_square.svg"
                alt="logo"
                data-testid="footer-logo"
              />
            </template>
          </SocioFooter>
        </template>
      </component>
    </RouterView>
    <VSnackbar
      v-model="newAppIsAvailable"
      :vertical="true"
      :location="true ? 'right' : undefined"
      timeout="-1"
      class="d-flex"
      data-testid="new-app-available-snackbar"
      style="gap: 8px; flex-direction: row !important"
    >
      <div class="content">{{ t("newAppText") }}</div>
      <VBtn color="blue" data-testid="refresh-app-btn" @click="update">
        {{ t("refresh") }}
      </VBtn>
    </VSnackbar>
  </VApp>
</template>

<script setup>
import { onMounted, ref, getCurrentInstance } from "vue";
import { useI18n } from "vue-i18n";
import useMainFooterItems from "./composables/navigations/useMainFooter";

const { t } = useI18n();
const instance = getCurrentInstance().proxy;
const newAppIsAvailable = ref(false);

onMounted(() => {
  if (instance.$workbox) {
    instance.$workbox.addEventListener("waiting", () => {
      newAppIsAvailable.value = true;
    });
  }
});
const update = async () => {
  newAppIsAvailable.value = false;
  if (instance.$workbox) {
    await instance.$workbox.messageSW({ type: "SKIP_WAITING" });
  }
};
</script>

<style lang="scss">
.detached-label {
  color: #333;
  font-size: 0.9rem;
  letter-spacing: 0;
}
.v-input--disabled {
  .v-input__control {
    .v-field__overlay {
      z-index: 0;
      background-color: #d5d5d5;
    }
  }
  .v-field__overlay {
    border: thin solid #181818;
    background-color: #e4e8fb !important;
    color: #989999;
  }
}
.domain-chip {
  background: #e6f3fc !important;
  border: 1px solid #0082de !important;
  box-sizing: border-box;
  border-radius: 50px;
}
.domain-pending-chip {
  color: rgb(255, 101, 51) !important;
  background-color: transparent !important;
  border: 1px solid rgb(255, 101, 51) !important;
  box-sizing: border-box;
  border-radius: 50px;
}
.green-chip {
  background: #edf9f2 !important;
  color: #00833f !important;
  border: 1px solid #00833f !important;
}
.orange-chip {
  background: #fff0eb !important;
  color: #ff6533 !important;
  border: 1px solid #ff6533 !important;
}
.yellow-chip {
  background: #fff8e6 !important;
  color: #fcb902 !important;
  border: 1px solid #fcb902 !important;
}
.red-chip {
  background: #fff0f0 !important;
  color: #d80f0c !important;
  border: 1px solid #d80f0c !important;
}
.normal-chip {
  background: #e0e0e0 !important;
  color: #000000 !important;
  border: 1px solid #e0e0e0 !important;
}
.v-label.v-field-label {
  margin-inline-start: 0.5rem !important;
}
.v-select .v-field--appended {
  padding-right: 0.5rem !important;
}
.v-btn--disabled {
  opacity: 0.4 !important;
  background-color: transparent !important;
  color: #333 !important;
}
.v-field__input {
  padding-inline-start: 0.5rem !important;
}
.v-text-field__suffix {
  opacity: 1 !important;
  font-size: 14px !important;
}
.v-input__details {
  padding: 0 !important;
  min-height: 16px !important;
}
.styleName {
  color: #333;
  line-height: 22px;
  letter-spacing: 0.1;
}
.vuetify-pro-tiptap {
  .vuetify-pro-tiptap-editor {
    box-shadow: 0 10px 5px -10px #585858;
  }
  .v-card-title {
    padding-left: 0;
    background-color: #fff !important;
    font-size: 1rem;
    font-weight: normal;
    color: #636363 !important;
  }
  .v-toolbar {
    background-color: #f5f5f5;
    border: thin solid #e0e0e0;
    border-radius: 6px 6px 0 0;
    border-bottom: none;
    .v-toolbar__content {
      display: flex;
      gap: 0.3rem;
      padding-left: 0.8rem;
    }
  }
  .v-divider {
    display: none;
  }
  .v-btn__content {
    .v-icon {
      font-size: 24px;
      color: #6b6b6b;
    }
  }
  .vuetify-pro-tiptap-editor__content {
    display: flex;
    align-items: center;
    max-height: 8rem;
    min-height: 4.5rem;
    line-height: 2.8;
    background-color: #fff !important;
    border: thin solid #e0e0e0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    border-top: none;
    padding-left: 1.5rem;
    cursor: pointer;
    .tiptap {
      flex: 1;
      min-height: inherit;
      &:focus-visible {
        outline: none !important;
      }
    }
    ~ .v-toolbar {
      background-color: #fff;
      border: none !important;
      display: none;
    }
  }
}
// A.L 07/08/26 depuis la migration SVC 8.2.2 -> 10.2.1 (CRTPRS-559), la lib supprime l'ombre de tous les dialogs
// ailleurs ça passe : un dialog s'ouvre sur un fond grisé qui le détache déjà de la page, pas ce panneau
// on lui remet donc l'ombre ici, à lui seul, les autres dialogs gardent le comportement de la lib
.socio-side-panel {
  border-radius: 4px;
  box-shadow:
    0 11px 15px -7px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
    0 24px 38px 3px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
    0 9px 46px 8px var(--v-shadow-key-ambient-opacity, rgba(0, 0, 0, 0.12));
}
a.tab-link.active {
  border-bottom: 3px solid #fcb902 !important;
}
.close-btn {
  &:hover {
    background-color: #fbfbfbfc;
    color: #23417c;
    border-radius: 0;
  }
}
.v-card-subtitle {
  white-space: break-spaces;
}
.h-94 {
  max-height: 94vh;
  overflow-y: auto;
}
@media screen and (max-width: 600px) {
  .h-94 {
    max-height: unset;
  }
}
</style>

<i18n>
{
  "en": {
    "refresh": "Refresh",
    "newAppText": "A new version of Certipers is available, please click refresh."
  },
  "fr": {
    "refresh": "Rafraîchir",
    "newAppText": "Une nouvelle version de Certipers est disponible, veuillez cliquer sur rafraîchir."
  }
}
</i18n>
=
